import type { Schema } from '@/shared/api/contract';

/** Straight from the contract: cubas.domain.schemas.admin and .triage. */
export type AdminUser = Schema<'AdminUserRead'>;
export type AdminUserCreate = Schema<'AdminUserCreate'>;
export type AdminUserCreated = Schema<'AdminUserCreated'>;
export type InviteRead = Schema<'InviteRead'>;
export type AdminUserUpdate = Schema<'AdminUserUpdate'>;
export type Mailbox = Schema<'MailboxRead'>;
export type MailboxList = Schema<'MailboxList'>;
export type MailboxCredentials = Schema<'MailboxCredentials'>;
export type MailboxWrite = Schema<'MailboxWrite'>;
export type MailboxUpdate = Schema<'MailboxUpdate'>;
export type ConnectionTest = Schema<'ConnectionTest'>;
export type TriageActivation = Schema<'TriageActivation'>;
export type ModeEntry = Schema<'ModeEntry'>;
export type ModesWrite = Schema<'ModesWrite'>;
export type MailboxProfile = Schema<'MailboxProfile'>;
export type MailboxOnConfirm = Schema<'MailboxOnConfirm'>;
export type AccessLogEntry = Schema<'AccessLogRead'>;
export type RetentionPreview = Schema<'RetentionPreview'>;
export type RetentionRun = Schema<'RetentionRun'>;
export type DocumentRetentionPolicy = Schema<'RetentionPolicyRead'>;
export type FeedbackSummary = Schema<'FeedbackSummary'>;
export type ErrorSummary = Schema<'ErrorSummary'>;
export type FeedbackAnswer = Schema<'FeedbackAnswer'>;
export type FeedbackAnswers = Schema<'FeedbackAnswers'>;

/** The list of answers of Administración → Correcciones (contract v10.33): the tab's
 * window plus its own filters. */
export interface FeedbackAnswerFilters extends FeedbackFilters {
  person?: string | undefined;
  signal?: string | undefined;
  with_note?: boolean | undefined;
  offset?: number | undefined;
}
export type ErrorGroup = Schema<'ErrorGroupRead'>;
export type ErrorEvent = Schema<'ErrorEventRead'>;
export type ReliabilityReport = Schema<'ReliabilityReport'>;
export type ReliabilityRow = Schema<'ReliabilityRow'>;
export type ReliabilityBand = Schema<'ReliabilityBand'>;
export type ReliabilityMailbox = Schema<'ReliabilityMailbox'>;
export type ReliabilityPerson = Schema<'ReliabilityPerson'>;

/** The window of Administración → Fiabilidad (contract v10.37). */
export interface ReliabilityFilters {
  days: number;
  current_prompt: boolean;
}

/** The window of Administración → Incidencias (contract v10.31). `since` an ISO instant. */
export interface ErrorFilters {
  since: string;
  code?: string | undefined;
  process?: string | undefined;
}

/** The window of Administración → Correcciones (contract v10.28). ISO instants; `until`
 * exclusive, like the API. */
export interface FeedbackFilters {
  since: string;
  until?: string | undefined;
  mailbox?: string | undefined;
}

/** The three files, by the path the API serves each under `/api/admin/feedback/`. */
export type FeedbackFile = 'triage.jsonl' | 'triage.csv' | 'extraction.jsonl';

/**
 * The events of the access log, as `cubas.domain.enums.AccessEvent` names them. The API
 * stores the event as text so adding one is not a migration; an event this list does not
 * know still shows, by its raw name.
 */
export const EVENT_LABELS: Record<string, string> = {
  login_ok: 'Entró',
  login_failed: 'Contraseña incorrecta',
  login_locked: 'Bloqueado por intentos',
  logout: 'Salió',
  password_changed: 'Cambió su contraseña',
  elevation: 'Modo elevado',
  user_created: 'Dio de alta a alguien',
  user_disabled: 'Desactivó a alguien',
  user_enabled: 'Reactivó a alguien',
  role_changed: 'Cambió permisos o buzones',
  password_reset: 'Puso una contraseña',
  mailbox_created: 'Registró un buzón',
  mailbox_changed: 'Cambió un buzón',
  mailbox_disabled: 'Dio de baja un buzón',
  mailbox_deleted: 'Borró un buzón',
  settings_changed: 'Cambió los ajustes',
  action_confirmed: 'Confirmó un reenvío',
  email_rescued: 'Rescató un correo de spam',
  email_trashed: 'Eliminó un correo',
  email_untrashed: 'Recuperó un correo',
  email_returned: 'Devolvió un correo a la bandeja',
  quote_discarded: 'Descartó un borrador',
  quote_emailed: 'Envió un presupuesto',
  feedback_exported: 'Descargó correcciones',
  errors_exported: 'Descargó incidencias',
  guideline_changed: 'Cambió un criterio aprendido',
  guideline_deleted: 'Borró un criterio aprendido',
  document_deleted: 'Borró un documento',
  document_replaced: 'Sustituyó un documento',
  document_restored: 'Recuperó un documento',
};

export const EVENTS: readonly string[] = Object.keys(EVENT_LABELS);

export function eventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event;
}

export interface AccessLogFilters {
  user_id?: string | undefined;
  event?: string | undefined;
  since?: string | undefined;
  until?: string | undefined;
  offset?: number | undefined;
}

/**
 * The rules a mailbox follows (contract v10.13): a set per person, written in the back
 * (`cubas.triage.routing`), chosen when the mailbox is registered. The name says whose
 * rules they are, not who reads the mailbox.
 */
/**
 * What «Correcto» does with the original in the mailbox (contract v10.25, ADR 0021 of
 * the back): files it in its folder, out of the inbox, or leaves it and copies.
 */
export const ON_CONFIRM_LABELS: Record<MailboxOnConfirm, string> = {
  move: 'Se ordena en el buzón',
  copy: 'Solo copiar',
};

export const ON_CONFIRM_HINTS: Record<MailboxOnConfirm, string> = {
  move: 'Al pulsar «Correcto», el correo sale de la bandeja de entrada del buzón y queda en su carpeta, leído. Es lo normal.',
  copy: 'Al pulsar «Correcto», se deja una copia en la carpeta y el original se queda en la bandeja de entrada. Para cuando algo no va bien.',
};

export const ON_CONFIRM: readonly MailboxOnConfirm[] = ['move', 'copy'];

export const PROFILE_LABELS: Record<MailboxProfile, string> = {
  victor: 'Reglas de Víctor (compras@ y ventas@)',
  ernesto: 'Reglas de Ernesto (ernesto@)',
  passive: 'Solo clasificar (administración@, maria@)',
};

export const PROFILES: readonly MailboxProfile[] = Object.keys(PROFILE_LABELS) as MailboxProfile[];
